
angular.module('acme.view.application.orders.offline.detail.controllers', [
  'ui.router',
  'ui.bootstrap',
  'acme.common.directive.dataGrid',
  'acme.common.service.offline',
  'acme.common.service.userPreferences'
]).controller('OfflineDetailController', OfflineDetailController);

/**
 * Controller responsible for managing the offline execution history list view
 * @class OfflineDetailController
 * @constructor
 * @param {Object} $q - angular's $q service
 * @param {Object} $uibModalInstance - angular's $uibModalInstance service
 * @param {Object} OfflineService - acme's OfflineService
 * @param {Object} HelpersService - acme's HelpersService
* @param {Object} UserPreferencesService - acme's UserPreferencesService
 * @param {Number} posUUID - posUUID that will be used to fetch the transactions history 
 * @return {OfflineDetailController} - Class instance
 */
function OfflineDetailController($q, $state, $uibModal, $uibModalInstance, OfflineService, HelpersService, UserPreferencesService, posUUID, orderNumber, orderId) {
  Object.assign(this, {
    $q: $q,
    $state: $state,
    $uibModal: $uibModal,
    $uibModalInstance: $uibModalInstance,
    OfflineService: OfflineService,
    HelpersService: HelpersService,
    UserPreferencesService: UserPreferencesService,
    posUUID: posUUID,
    orderNumber: orderNumber,
    orderId: orderId
  });
  this.canRerun = ['FAILED'];
  this.dataChanged = false;
  this.vm = {};
  this.formatters = {
    DATE: 'date:":format"'.replace(':format', this.UserPreferencesService.getDateTimeFormat('sortDateTime'))
  };
  this.transactionDetails = {
    columns: 2,
      data: '$ctrl.vm.transaction',
      include: [
      'status',
      'deleted',
      'terminalId',
      'posUUID',
      'orderDetail',
      'orderLink'
    ],
    mappings: {
      'id': 'Transaction ID',
      'orderLink': 'Order Detail',
      'posUUID': 'Point of Sale UUID'
    },
    clickHandler: {
      'orderLink': function () {
        $state.go('application.orders.detail.summary', { orderId: orderId });
      },
      'orderDetail': function() {
        $uibModalInstance.close();
        $uibModal.open({
          templateUrl:
              'views/application/views/orders/offline/list/order-detail.tpl.html',
          size: 'lg',
          controller: 'OfflineOrderDetailController',
          controllerAs: '$ctrl',
          resolve: {
            posUUID: function() {
              return posUUID;
            },
            orderNumber: function() {
              return orderNumber;
            },
            orderId: function() {
              return orderId;
            }
          }
        });
      }
    }
  };

  // TODO: update ui-router to 0.3.0 to avoid calling this manually
  this.$onInit();
}

/**
 * Angular lifecycle method $onInit
 * @public
 * @memberOf OfflineDetailController
 * @return {Undefined} -
 */
OfflineDetailController.prototype.$onInit = function () {
  this.loading = false;
  this.vm = this._getVm();
  this.refresh();
};

/**
 * Loads the history and the detail for a particular posUUID
 * Updates the UI
 * @private
 * @memberOf OfflineDetailController
 * @param {Number=} posUUID - transaction posUUID
 * @return {Promise} -
 */
OfflineDetailController.prototype._load = function (posUUID) {
  var getVm = this._getVm.bind(this),
    updateView = this._updateView.bind(this),
    processData = this._processData.bind(this),
    request = this._executionHistory(posUUID);

  this.loading = true;
  return request
    .then(processData)
    .then(getVm)
    .then(updateView)
    .catch(updateView);
};

/**
 * Request the server for the executions history
 * @memberOf OfflineDetailController
 * @param {Object} line - row data
 * @private
 * @return {{transaction: Object, history: Array}} - aggregate data
 */
OfflineDetailController.prototype._processData = function (responses) {
  var _responses = responses || [];

  var transaction = _responses[0];
  if (transaction) {
    if (transaction.status === "success" && !!this.orderNumber) {
      transaction.orderLink = '<a class="clickable">' + this.orderNumber + '</a>';
    } else {
      transaction.orderDetail = '<a class="offline-transactions-detail-order-button">View Order Details</a>';
    }
  }

  return {
    transaction: transaction,
    history: _responses[1]
  };
};

/**
 * Request the server for the executions history
 * @memberOf OfflineDetailController
 * @param {Number=} posUUID - transaction posUUID
 * @private
 * @return {Promise} -
 */
OfflineDetailController.prototype._executionHistory = function(posUUID) {
  var request = this.$q.resolve();

  if (posUUID) {
    request = this.$q.all([
      this.OfflineService.find(posUUID),
      this.OfflineService.executions(posUUID)
    ]);
  }

  return request;
};

/**
 * Process the ViewModel based on the passed data
 * @memberOf OfflineDetailController
 * @param {{transaction: Object, history: Array}} - data used to compute the viewModel
 * @private
 * @return {Object} - the viewModel
 */
OfflineDetailController.prototype._getVm = function (data) {
  var _data = data || {},
    lines = _data.history || [],
    transaction = _data.transaction || {},
    status = transaction.status || '',
    pageSize = this.vm.pageSize,
    page = this.vm.page,
    sortDirection = this.vm.sortDirection || 'desc',
    sortField = this.vm.sortField || 'updatedOn',
    paginate = this._paginate.bind(this),
    sort = this._sort.bind(this),
    done = this.dataChanged && this.$uibModalInstance.close || this.$uibModalInstance.dismiss,
    canRerun = false,
    columns = [];

  columns = this._buildColumns(this.formatters);
  lines = this._buildLines(lines);
  canRerun = this.canRerun.indexOf(status.toUpperCase()) > -1;

  return {
    transaction: transaction,
    columns: columns,
    pageSize: parseInt(pageSize, 10) || 10,
    page: parseInt(page, 10) || 1,
    sortDirection: sortDirection,
    sortField: sortField,
    hasData: lines.length > 0,
    canRerun: canRerun,
    count: lines.length,
    lines: lines,
    sort: sort,
    paginate: paginate,
    done: done
  };
};

/**
 * Sets the sorted properties and refreshes the view
 * @memberOf OfflineDetailController
 * @param {Object} grid - grid api object
 * @param {Array<Object>} columnData - Array with the sorted column
 * @private
 * @return {Undefined} -
 */
OfflineDetailController.prototype._sort = function (grid, columnData) {
  var column = columnData && columnData[0] || {
    sort: {}
  };

  this.vm.sortField = column.sortField;
  this.vm.sortDirection = column.sort.direction;
};

/**
 * Sets the paginations properties and refreshes the view
 * @memberOf OfflineDetailController
 * @param {Number} page - page number requested
 * @param {String} pageSize - number of items to display
 * @private
 * @return {Undefined} -
 */
OfflineDetailController.prototype._paginate = function (page, pageSize) {
  var _page = parseInt(page, 10) || this.vm.page,
    _pageSize = parseInt(pageSize, 10) || this.vm.pageSize;

  if (this.vm.page !== _page || this.vm.pageSize !== _pageSize) {
    this.vm.page = _page;
    this.vm.pageSize = _pageSize;
  }
};

/**
 * Tries to rerun a failed transaction
 * @memberOf OfflineDetailController
 * @public
 * @return {Promise} -
 */
OfflineDetailController.prototype.rerun = function () {
  var reload = this.refresh.bind(this),
    posUUID = this.posUUID,
    request = this.$q.resolve();

  if(posUUID) {
    this.dataChanged = true;
    request = this.OfflineService.rerun(this.posUUID)
      .then(reload);
  }

  return request;
};

/**
 * Refreshes the view
 * @memberOf OfflineDetailController
 * @public
 * @return {Undefined} -
 */
OfflineDetailController.prototype.refresh = function () {
  this._load(this.posUUID);
};

/**
 * Request the server for the executions history
 * @memberOf OfflineDetailController
 * @param {Number=} posUUID - transaction posUUID
 * @private
 * @return {Promise} -
 */
OfflineDetailController.prototype._detail = function (posUUID) {
  var request = this.$q.resolve();

  if (posUUID) {
    request = this.OfflineService.executions(posUUID);
  }

  return request;
};

/**
 * Builds lines for the UI
 * @memberOf OfflineDetailController
 * @param {Array} lines - rows data
 * @private
 * @return {Array} -
 */
OfflineDetailController.prototype._buildLines = function (lines) {
  var _lines = lines || [];

  return _lines
    .map(function(line) {
      var _line = line || {},
        hasStatus = _line.beforeStatus && _line.afterStatus,
        status = hasStatus ? [_line.beforeStatus, _line.afterStatus].join(' → ') : '';

      return Object.assign({}, line, {status: status});
    });
};

/**
 * Builds the colum definitions for the ag-grid
 * @memberOf OfflineDetailController
 * @param {Object} formatters - Cellfilter formatters
 * @private
 * @return {Array.<ColumnDefs>} - Column definitions
 */
OfflineDetailController.prototype._buildColumns = function (formatters) {
    var _formatters = formatters || {},
    defaultColumns = [
      {
        displayName: 'id',
        field: 'id',
        type: 'number',
        sort: {
          direction: 'desc'
        }
      },
      {
        displayName: 'created on',
        field: 'createdOn',
        cellFilter: _formatters.DATE
      },
      {
        displayName: 'updated on',
        field: 'updatedOn',
        cellFilter: _formatters.DATE,
      },
      {
        displayName: 'transition',
        field: 'status',
        enableSorting: false,
        cellClass: 'offline-transactions-detail-row-style'
      },
      {
        displayName: 'detail',
        enableSorting: false,
        field: 'detail'
      }
    ];

    return defaultColumns;
};

/**
 * Updates the viewModel
 * @memberOf OfflineDetailController
 * @param {Object} vm - Object containing the data to update the public vm property
 * @private
 * @return {Undefined} -
 */
OfflineDetailController.prototype._updateView = function (vm) {
  this.vm = Object.assign({}, this.vm, vm);
  this.loading = false;
};

OfflineDetailController.$inject = [
  '$q',
  '$state',
  '$uibModal',
  '$uibModalInstance',
  'OfflineService',
  'HelpersService',
  'UserPreferencesService',
  'posUUID',
  'orderNumber',
  'orderId'
];
